/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { useState } from 'react'
import { X } from 'lucide-react'
import { PERSONA_LIBRARY, PERSONA_CATEGORIES } from '@/core/personas'
import { OPENROUTER_MODELS } from '@/providers/llm-provider'
import type { PeerConfig } from '@/lib/storage'

interface PeerSettingsModalProps {
  peer: PeerConfig
  onClose: () => void
  onSave: (updatedPeer: PeerConfig) => void
}

export function PeerSettingsModal({ peer, onClose, onSave }: PeerSettingsModalProps) {
  const [selectedPersonaId, setSelectedPersonaId] = useState(peer.personaId)
  const [selectedModel, setSelectedModel] = useState(peer.model)
  const [systemPrompt, setSystemPrompt] = useState(peer.systemPrompt)
  const [selectedCategory, setSelectedCategory] = useState<string>('All')
  const [preserveContext, setPreserveContext] = useState(peer.preserveContext ?? false)

  // Get persona details
  const selectedPersona = PERSONA_LIBRARY.find(p => p.id === selectedPersonaId)

  // Filter personas by category
  const filteredPersonas = selectedCategory === 'All'
    ? PERSONA_LIBRARY
    : PERSONA_LIBRARY.filter(p => p.category === selectedCategory)

  // Debug logging
  console.log('🔍 Filter Debug:', {
    selectedCategory,
    totalPersonas: PERSONA_LIBRARY.length,
    filteredCount: filteredPersonas.length,
    categories: PERSONA_CATEGORIES,
    firstSixFiltered: filteredPersonas.slice(0, 6).map(p => ({ name: p.name, category: p.category })),
    allCategories: [...new Set(PERSONA_LIBRARY.map(p => p.category))]
  })

  // Handle persona change
  const handlePersonaChange = (personaId: string) => {
    setSelectedPersonaId(personaId)
    const persona = PERSONA_LIBRARY.find(p => p.id === personaId)
    if (persona) {
      // Update system prompt with new persona template
      const newPrompt = persona.systemPromptTemplate
        .replace(/{name}/g, persona.name)
        .replace(/{role}/g, persona.role)
      setSystemPrompt(newPrompt)
    }
  }

  // Handle save
  const handleSave = () => {
    const persona = PERSONA_LIBRARY.find(p => p.id === selectedPersonaId)
    if (!persona) return

    const updatedPeer: PeerConfig = {
      ...peer,
      personaId: selectedPersonaId,
      personaName: persona.name,
      model: selectedModel,
      systemPrompt,
      preserveContext,
    }

    onSave(updatedPeer)
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-background-secondary border border-border rounded-lg w-[800px] max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="border-b border-border p-4 flex items-center justify-between">
          <h2 className="text-lg font-title">Peer Settings: {peer.personaName}</h2>
          <button onClick={onClose} className="p-1 hover:bg-background-tertiary rounded transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4 space-y-4">
          {/* Persona Selection */}
          <div>
            <label className="block text-sm font-semibold mb-2">Persona</label>
            
            {/* Category Filter */}
            <div className="flex gap-2 mb-3 flex-wrap">
              <button
                onClick={() => setSelectedCategory('All')}
                className={`px-3 py-1 text-xs rounded transition-colors ${
                  selectedCategory === 'All'
                    ? 'bg-accent text-background font-semibold'
                    : 'bg-background-tertiary text-foreground hover:bg-background-hover'
                }`}
              >
                All ({PERSONA_LIBRARY.length})
              </button>
              {PERSONA_CATEGORIES.map(category => {
                const count = PERSONA_LIBRARY.filter(p => p.category === category).length
                return (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-3 py-1 text-xs rounded transition-colors ${
                      selectedCategory === category
                        ? 'bg-accent text-background font-semibold'
                        : 'bg-background-tertiary text-foreground hover:bg-background-hover'
                    }`}
                  >
                    {category} ({count})
                  </button>
                )
              })}
            </div>

            {/* Persona Grid */}
            <div
              key={selectedCategory}
              className="grid grid-cols-2 gap-2 max-h-[200px] overflow-auto border border-border rounded p-2"
            >
              {filteredPersonas.length > 0 ? (
                filteredPersonas.map(persona => (
                  <button
                    key={`${selectedCategory}-${persona.id}`}
                    onClick={() => handlePersonaChange(persona.id)}
                    className={`text-left p-3 rounded border transition-colors ${
                      selectedPersonaId === persona.id
                        ? 'border-accent bg-accent/10'
                        : 'border-border hover:border-border-secondary'
                    }`}
                  >
                    <div className="font-semibold text-sm">{persona.name}</div>
                    <div className="text-xs text-foreground-muted">{persona.role}</div>
                    <div className="text-[10px] text-foreground-muted mt-1">{persona.category}</div>
                  </button>
                ))
              ) : (
                <div className="col-span-2 text-center text-foreground-muted text-sm py-8">
                  No personas found in this category
                </div>
              )}
            </div>

            {/* Persona Description */}
            {selectedPersona && (
              <div className="mt-2 p-3 bg-background-tertiary rounded text-xs">
                <div className="font-semibold mb-1">{selectedPersona.name}</div>
                <div className="text-foreground-muted">{selectedPersona.description}</div>
              </div>
            )}
          </div>

          {/* Model Selection */}
          <div>
            <label className="block text-sm font-semibold mb-2">Model</label>
            <select
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              className="w-full bg-background border border-border rounded px-3 py-2 text-sm"
            >
              <optgroup label="💎 Premium - Best in Class ($3-15/M tokens)">
                <option value="anthropic/claude-sonnet-4.5">Claude Sonnet 4.5 (200K, $3-15/M) - BEST OVERALL</option>
                <option value="openai/gpt-5">GPT-5 (128K, $5-15/M)</option>
                <option value="anthropic/claude-3-opus-20240229">Claude 3 Opus (200K, $15-75/M)</option>
              </optgroup>
              <optgroup label="⭐ Premium Affordable ($1-5/M tokens)">
                <option value="google/gemini-2.5-pro">Gemini 2.5 Pro (2M context!, $1.25-5/M) - RECOMMENDED</option>
                <option value="anthropic/claude-3-5-sonnet-20241022">Claude 3.5 Sonnet (200K, $3-15/M)</option>
                <option value="openai/gpt-4o">GPT-4o (128K, $2.50-10/M)</option>
                <option value="openai/gpt-4-turbo">GPT-4 Turbo (128K, $10-30/M)</option>
              </optgroup>
              <optgroup label="💰 Budget - Great Value ($0.15-1/M tokens)">
                <option value="google/gemini-2.0-flash-exp">Gemini 2.0 Flash (1M context!, $0.30-1.20/M) - BEST BUDGET</option>
                <option value="openai/gpt-4o-mini">GPT-4o Mini (128K, $0.15-0.60/M)</option>
                <option value="anthropic/claude-3-haiku-20240307">Claude 3 Haiku (200K, $0.80-4/M)</option>
              </optgroup>
              <optgroup label="💸 Ultra-Cheap - US Based ($0.18-0.20/M tokens)">
                <option value="groq/llama-3.3-70b-versatile">Groq Llama 3.3 70B (128K, $0.20/M) - BLAZING FAST ⚡</option>
                <option value="together/meta-llama/Meta-Llama-3.1-70B-Instruct-Turbo">Together Llama 3.1 70B (131K, $0.18/M)</option>
                <option value="groq/mixtral-8x7b-32768">Groq Mixtral 8x7B (32K, $0.20/M)</option>
                <option value="groq/llama3-8b-8192">Groq Llama 3 8B (8K, $0.20/M) - ULTRA FAST ⚡⚡</option>
              </optgroup>
              <optgroup label="🔥 Ultra-Cheap - Absolute Lowest ($0.01-0.10/M tokens)">
                <option value="deepseek/deepseek-chat">DeepSeek Chat V3 (64K, $0.014-0.028/M) - CHEAPEST 💰</option>
                <option value="qwen/qwen3-235b-a22b">Qwen 3 235B-A22B MoE (128K, $0.10/M) - THINKING MODE 🧠</option>
                <option value="qwen/qwen3-30b-a3b">Qwen 3 30B-A3B MoE (128K, $0.08/M) - THINKING MODE 🧠</option>
                <option value="qwen/qwen3-32b">Qwen 3 32B (128K, $0.09/M) - NEW 2025</option>
                <option value="siliconflow/Qwen/Qwen2.5-72B-Instruct">SiliconFlow Qwen 2.5 72B (32K, $0.086/M)</option>
                <option value="together/meta-llama/Meta-Llama-3.1-8B-Instruct-Turbo">Together Llama 3.1 8B (131K, $0.06/M)</option>
                <option value="siliconflow/Qwen/Qwen2.5-7B-Instruct">SiliconFlow Qwen 2.5 7B (32K, $0.05/M)</option>
              </optgroup>
              <optgroup label="🔓 Open-Source - Large Models">
                <option value="meta-llama/llama-3.1-405b-instruct">Llama 3.1 405B (128K)</option>
                <option value="meta-llama/llama-3.1-70b-instruct">Llama 3.1 70B (128K)</option>
                <option value="qwen/qwen-2.5-72b-instruct">Qwen 2.5 72B (32K)</option>
                <option value="qwen/qwen3-14b">Qwen 3 14B (128K) - NEW 2025</option>
                <option value="qwen/qwen3-8b">Qwen 3 8B (128K) - NEW 2025</option>
              </optgroup>
              <optgroup label="🌐 All Models (Alphabetical)">
                {OPENROUTER_MODELS.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.name} ({model.provider})
                  </option>
                ))}
              </optgroup>
            </select>
            {selectedPersona && selectedPersona.recommendedModels.length > 0 && (
              <div className="mt-2 text-xs text-foreground-muted">
                Recommended: {selectedPersona.recommendedModels.join(', ')}
              </div>
            )}
          </div>

          {/* System Prompt */}
          <div>
            <label className="block text-sm font-semibold mb-2">System Prompt (MOTH Format)</label>
            <textarea
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              className="w-full bg-background border border-border rounded px-3 py-2 text-xs font-mono h-[300px]"
              placeholder="Enter system prompt in MOTH format..."
            />
            <div className="mt-1 text-xs text-foreground-muted">
              Edit the system prompt to customize this peer's behavior. Use MOTH format with [SECTION] headers.
            </div>
          </div>

          {/* Preserve Context Toggle */}
          <div className="flex items-center justify-between p-3 bg-background-tertiary rounded border border-border">
            <div>
              <div className="text-sm font-semibold">Preserve Conversation Context</div>
              <div className="text-xs text-foreground-muted mt-1">
                When enabled, this peer will receive the full conversation history from previous orchestrations.
                This allows the peer to build on previous feedback but increases token usage.
              </div>
            </div>
            <button
              onClick={() => setPreserveContext(!preserveContext)}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                preserveContext ? 'bg-accent' : 'bg-border'
              }`}
            >
              <div
                className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                  preserveContext ? 'translate-x-6' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-border p-4 flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost px-4 py-2">
            Cancel
          </button>
          <button onClick={handleSave} className="btn-primary px-4 py-2">
            Save Changes
          </button>
        </div>
      </div>
    </div>
  )
}

